import { CreditCard } from "lucide-react";
import {
  APICategory,
  BlueprintSpeed,
  Organization,
  UnreleasedAPICategory,
} from "../../../../models/Entities";

export const LAUNCH_GUARDRAILS_LINKED_ACCOUNT_LIMIT = 10;

export enum BillingPlanTier {
  BILLING_PLAN_TIER_FREE = "FREE",
  BILLING_PLAN_TIER_LAUNCH = "LAUNCH",
  BILLING_PLAN_TIER_GROW = "GROW",
  BILLING_PLAN_TIER_EXPAND = "EXPAND",
  BILLING_PLAN_TIER_CORE = "CORE",
  BILLING_PLAN_TIER_CORE_STARTUP = "STARTUP_CORE",
  BILLING_PLAN_TIER_PROFESSIONAL = "PROFESSIONAL",
  BILLING_PLAN_TIER_PROFESSIONAL_STARTUP = "STARTUP_PROFESSIONAL",
  BILLING_PLAN_TIER_ENTERPRISE = "ENTERPRISE",
}

export interface BillingPlan {
  id: string;
  name: string;
  is_active: boolean;
  plan_tier: BillingPlanTier;

  // Plan Pricing
  monthly_discount_cents: number;
  monthly_price_cents: number;
  api_request_price_cents: number;
  scrape_price_cents: number;
  webhook_price_cents: number;

  // Plan Caps
  free_api_requests: number;
  free_scrapes: number;
  free_webhooks: number;
  product_restrictions: {
    test_accounts: number;
  };

  /**
   * Price of each Common Model, in cents.
   * Example: { "ats.Application": 0.1, "ats.Candidate": 0.15, "hris.Employee": 50 }
   */
  common_model_prices_cents: {
    [key: string]: number;
  };
  /**
   * Price of each linked account, in cents.
   * Example: { "accounting": 2000 }
   */
  linked_account_category_prices_cents: {
    [key: string]: number;
  };
}

export interface ProductRestrictions {
  id: string;
  available_sync_speeds: BlueprintSpeed[];
  log_retention_days: number;
  test_accounts: number;
  webhooks_enabled: boolean;
  are_toggles_enabled: boolean;
  are_remote_data_toggles_enabled: boolean;
  are_custom_fields_enabled: boolean;
  are_custom_objects_enabled: boolean;
  third_party_webhooks_enabled: boolean;
  passthrough_requests_enabled: boolean;
  redact_unmapped_data_enabled: boolean;
  audit_log_enabled: boolean;
  customizable_link_enabled: boolean;
  is_sftp_enabled: boolean;
}

export interface OrganizationBillingPlan {
  id: string;
  organization: Organization;
  billing_plan: BillingPlan;
  start_date: string;
  end_date: string;
  product_restrictions: ProductRestrictions;
  should_auto_charge: boolean;
  deletion_detection_enabled: boolean;
}

export interface OrganizationBillingPlanCounts {
  linked_accounts_count: Record<string, number>; // Keys are category
  test_accounts_count: number;
}

/**
 * The values calculated per category on a specific invoice.
 */
export interface CategoryCharge {
  category: APICategory | UnreleasedAPICategory;
  unit: string;
  priceCents: number;
  quantity?: number;
  totalCostCents?: number;
}

/**
 * The values calculated for a category with current prices and quantities.
 */
export type CurrentCategoryCharge = Omit<CategoryCharge, "totalCostCents">;

export enum PaymentMethodTypes {
  CREDIT_CARD = "CREDIT_CARD",
  BANK_ACCOUNT = "BANK_ACCOUNT",
}
export interface BankAccount {
  id: string;
  last4: string;
  is_default_method: boolean;
  created_at: string;
  bank_name: string;
}
export interface CreditCard {
  id: string;
  brand: string;
  last4: string;
  exp_month: string;
  exp_year: string;
  is_default_method: boolean;
  created_at: string;
}

export type PaymentMethod = BankAccount | CreditCard;

export const isCreditCard = (x: PaymentMethod): x is CreditCard => x.hasOwnProperty("brand");

export enum MergeChargeStatus {
  CHARGE_STATUS_SUCCESS = "SUCCESS",
  CHARGE_STATUS_SUSPENDED = "SUSPENDED",
  CHARGE_STATUS_PENDING = "PENDING",
  CHARGE_STATUS_FAILURE = "FAILURE",
  CHARGE_STATUS_WAIVED = "WAIVED",
  CHARGE_STATUS_OVERDUE = "OVERDUE",
}
